import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Shield, Save, Loader2, Lock, Building2 } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';

const AgencyPicker = ({ agencies, value, onChange }) => (
  <Select value={value || ''} onValueChange={onChange}>
    <SelectTrigger className="w-72 rounded-xl" data-testid="access-agency-select">
      <Building2 className="w-4 h-4 mr-2 text-[#B85C38]" />
      <SelectValue placeholder="Choisir une agence" />
    </SelectTrigger>
    <SelectContent>
      {agencies.map((a) => (
        <SelectItem key={a.id} value={a.id} data-testid={`access-agency-${a.id}`}>{a.name || a.id}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const RoleCell = ({ allowed, locked, lockLabel, editable, onToggle, testId }) => (
  <div className="flex items-center justify-center gap-2 min-w-[150px]">
    {locked ? (
      <Badge className="bg-rose-50 text-rose-700 border border-rose-200 font-normal" data-testid={`${testId}-locked`}>
        <Lock className="w-3 h-3 mr-1" /> {lockLabel}
      </Badge>
    ) : editable ? (
      <>
        <Switch checked={allowed} onCheckedChange={onToggle} data-testid={testId} />
        <span className={`text-xs w-16 ${allowed ? 'text-emerald-700' : 'text-gray-500'}`}>{allowed ? 'Autorisé' : 'Bloqué'}</span>
      </>
    ) : (
      <Badge className={`${allowed ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-600 border-gray-200'} border font-normal`} data-testid={`${testId}-readonly`}>
        {allowed ? 'Autorisé' : 'Bloqué'}
      </Badge>
    )}
  </div>
);

const AgentPermissions = () => {
  const { user } = useAuth();
  const isSuper = user?.role === 'super_admin';
  const isAdmin = isSuper || user?.role === 'admin';
  const [catalog, setCatalog] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [agencyId, setAgencyId] = useState(isSuper ? null : user?.agency_id);
  const [policy, setPolicy] = useState(null);
  const [blockedAdmin, setBlockedAdmin] = useState(new Set());
  const [blockedAgent, setBlockedAgent] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    api.get('/access/catalog').then((r) => setCatalog(r.data || [])).catch(() => toast.error('Erreur de chargement'));
    if (isSuper) {
      api.get('/access/agencies').then((r) => {
        const list = r.data || [];
        setAgencies(list);
        setAgencyId((cur) => cur || user?.agency_id || list[0]?.id || null);
      }).catch(() => toast.error('Erreur de chargement des agences'));
    }
  }, [isAdmin, isSuper, user]);

  const applyPolicy = useCallback((p) => {
    setPolicy(p);
    setBlockedAdmin(new Set(p.super_blocked.admin));
    setBlockedAgent(new Set(isSuper ? p.super_blocked.agent : p.admin_blocked.agent));
  }, [isSuper]);

  useEffect(() => {
    if (!isAdmin || !agencyId) { setLoading(false); return; }
    setLoading(true);
    api.get('/access/policy', { params: { agency_id: agencyId } })
      .then((r) => applyPolicy(r.data))
      .catch(() => toast.error('Erreur lors du chargement des droits'))
      .finally(() => setLoading(false));
  }, [isAdmin, agencyId, applyPolicy]);

  const sections = useMemo(() => {
    const map = {};
    catalog.forEach((m) => { (map[m.section] = map[m.section] || []).push(m); });
    return Object.entries(map);
  }, [catalog]);

  const toggle = (setter) => (key) => setter((prev) => {
    const n = new Set(prev);
    n.has(key) ? n.delete(key) : n.add(key);
    return n;
  });

  const save = async () => {
    setSaving(true);
    try {
      let r;
      if (isSuper) {
        await api.put('/access/policy', { agency_id: agencyId, role: 'admin', blocked: [...blockedAdmin] });
        r = await api.put('/access/policy', { agency_id: agencyId, role: 'agent', blocked: [...blockedAgent] });
      } else {
        r = await api.put('/access/policy', { role: 'agent', blocked: [...blockedAgent] });
      }
      applyPolicy(r.data);
      toast.success('Droits enregistrés. Ils s’appliquent dès le prochain rafraîchissement des utilisateurs concernés.');
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return <div className="flex items-center justify-center h-64"><p className="text-gray-500">Accès réservé aux administrateurs</p></div>;
  }

  const superAgent = new Set(policy?.super_blocked.agent || []);
  const adminAgent = new Set(policy?.admin_blocked.agent || []);

  return (
    <div className="space-y-6" data-testid="access-rights-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#2B3A2C] flex items-center gap-2">
            <Shield className="w-7 h-7 text-[#B85C38]" /> Droits d&apos;accès
          </h1>
          <p className="text-sm text-gray-500 mt-1 font-body-modern max-w-2xl">
            Agents, admins et super admin partagent les mêmes données de l&apos;agence.
            {isSuper ? ' Bloquez ici les modules que les admins et/ou les agents ne doivent pas voir.' : ' Vous pouvez fermer des modules à vos agents, dans la limite fixée par le super admin.'}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {isSuper && <AgencyPicker agencies={agencies} value={agencyId} onChange={setAgencyId} />}
          <Button onClick={save} disabled={saving || loading || !agencyId} className="bg-[#B85C38] hover:bg-[#a04d2f] rounded-xl" data-testid="save-access-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Enregistrer
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48"><Loader2 className="w-6 h-6 animate-spin text-[#B85C38]" /></div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#B85C38]/15 shadow-sm overflow-x-auto">
          <table className="w-full text-sm font-body-modern" data-testid="access-matrix">
            <thead>
              <tr className="bg-[#2B3A2C] text-white">
                <th className="text-left px-4 py-3 font-semibold">Module</th>
                <th className="px-4 py-3 font-semibold">Administrateurs</th>
                <th className="px-4 py-3 font-semibold">Agents</th>
              </tr>
            </thead>
            <tbody>
              {sections.map(([section, mods]) => (
                <React.Fragment key={section}>
                  <tr className="bg-[#F5EFE1]">
                    <td colSpan={3} className="px-4 py-2 text-[11px] uppercase tracking-wider font-bold text-[#B85C38]">{section}</td>
                  </tr>
                  {mods.map((m) => (
                    <tr key={m.key} className="border-b border-gray-100 hover:bg-gray-50/60" data-testid={`access-row-${m.key}`}>
                      <td className="px-4 py-2.5 text-[#2B3A2C] font-medium">{m.label}</td>
                      <td className="px-4 py-2.5">
                        <RoleCell
                          allowed={!blockedAdmin.has(m.key)}
                          editable={isSuper}
                          onToggle={() => toggle(setBlockedAdmin)(m.key)}
                          testId={`access-admin-${m.key}`}
                        />
                      </td>
                      <td className="px-4 py-2.5">
                        <RoleCell
                          allowed={!blockedAgent.has(m.key)}
                          editable
                          locked={!isSuper && superAgent.has(m.key)}
                          lockLabel="Bloqué par le super admin"
                          onToggle={() => toggle(setBlockedAgent)(m.key)}
                          testId={`access-agent-${m.key}`}
                        />
                        {isSuper && adminAgent.has(m.key) && !blockedAgent.has(m.key) && (
                          <p className="text-[10px] text-amber-700 text-center mt-1">Fermé par l&apos;admin de l&apos;agence</p>
                        )}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900 font-body-modern">
        <p className="font-semibold mb-1">Bon à savoir</p>
        <ul className="list-disc list-inside space-y-1 text-amber-800">
          <li>Par défaut, tout est autorisé : agents et admins voient les mêmes données que le super admin pour leur agence.</li>
          <li>Un module bloqué disparaît du menu et ses données sont refusées par le serveur.</li>
          <li>Biens, Clients, Transactions, Tableau de bord et Mon Espace RH restent toujours accessibles. La gestion des utilisateurs et des droits reste réservée aux admins.</li>
        </ul>
      </div>
    </div>
  );
};

export default AgentPermissions;
